import { useState, useCallback } from "react";
import {
  ACTOR_ID_HEADER_KEY,
  AUTHORIZATION_HEADER_KEY,
  EMAIL_SERVICE_BASE_URL,
} from "constants";
import apiClient from "utils/apiClient";
import { useAuth } from "contexts/AuthContext";

const EMAILS_BASE_URL = `${EMAIL_SERVICE_BASE_URL}/api/v1/emails`;
const EMAIL_ACCOUNTS_BASE_URL = `${EMAIL_SERVICE_BASE_URL}/api/v1/email-accounts`;
const CORRELATION_ID_HEADER_KEY = "O-CORRELATION-ID";

const useInhouseEmails = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const getAuthHeaders = useCallback(
    ({ actorId, token, correlationId } = {}) => {
      const authToken = token ?? sessionStorage.getItem("authToken");
      const headers = {
        [ACTOR_ID_HEADER_KEY]: actorId || user?.id || "",
        [AUTHORIZATION_HEADER_KEY]: authToken ? `Bearer ${authToken}` : "",
      };

      if (correlationId) {
        headers[CORRELATION_ID_HEADER_KEY] = correlationId;
      }

      return headers;
    },
    [user?.id],
  );

  const request = useCallback(async (method, url, { data, params, headers } = {}) => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.request({
        method,
        url,
        data,
        params,
        headers,
      });
      return response.data;
    } catch (err) {
      const errorMessage =
        err.response?.data?.message || err.response?.data?.error || err.message;
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  // Emails
  const getAllEmails = useCallback(
    async (options = {}) =>
      request("get", EMAILS_BASE_URL, {
        headers: getAuthHeaders(options),
      }),
    [getAuthHeaders, request],
  );

  const getEmailById = useCallback(
    async (id, options = {}) =>
      request("get", `${EMAILS_BASE_URL}/${id}`, {
        headers: getAuthHeaders(options),
      }),
    [getAuthHeaders, request],
  );

  const sendEmail = useCallback(
    async (payload, options = {}) =>
      request("post", EMAILS_BASE_URL, {
        data: payload,
        headers: getAuthHeaders(options),
      }),
    [getAuthHeaders, request],
  );

  const updateEmail = useCallback(
    async (id, payload, options = {}) =>
      request("put", `${EMAILS_BASE_URL}/${id}`, {
        data: payload,
        headers: getAuthHeaders(options),
      }),
    [getAuthHeaders, request],
  );

  const deleteEmail = useCallback(
    async (id, options = {}) =>
      request("delete", `${EMAILS_BASE_URL}/${id}`, {
        headers: getAuthHeaders(options),
      }),
    [getAuthHeaders, request],
  );

  const deleteEmails = useCallback(
    async (options = {}) =>
      request("delete", `${EMAILS_BASE_URL}/bulk`, {
        headers: getAuthHeaders(options),
      }),
    [getAuthHeaders, request],
  );

  const getIncomingEmailsByAccountId = useCallback(
    async ({ page = 0, size = 10, ...options } = {}) =>
      request("get", `${EMAILS_BASE_URL}/incoming`, {
        headers: getAuthHeaders(options),
        params: { page, size },
      }),
    [getAuthHeaders, request],
  );

  const getOutgoingEmailsByAccountId = useCallback(
    async ({ page = 0, size = 10, ...options } = {}) =>
      request("get", `${EMAILS_BASE_URL}/outgoing`, {
        headers: getAuthHeaders(options),
        params: { page, size },
      }),
    [getAuthHeaders, request],
  );

  const getIncomingEmailsByEmail = useCallback(
    async ({ email, page = 0, size = 10, ...options } = {}) =>
      request("get", `${EMAILS_BASE_URL}/incoming/email`, {
        headers: getAuthHeaders(options),
        params: { email, page, size },
      }),
    [getAuthHeaders, request],
  );

  const getOutgoingEmailsByEmail = useCallback(
    async ({ email, page = 0, size = 10, ...options } = {}) =>
      request("get", `${EMAILS_BASE_URL}/outgoing/email`, {
        headers: getAuthHeaders(options),
        params: { email, page, size },
      }),
    [getAuthHeaders, request],
  );

  const countUnreadEmails = useCallback(
    async (options = {}) =>
      request("get", `${EMAILS_BASE_URL}/unread/count`, {
        headers: getAuthHeaders(options),
      }),
    [getAuthHeaders, request],
  );

  // Email accounts
  const getAllAccounts = useCallback(
    async (options = {}) =>
      request("get", EMAIL_ACCOUNTS_BASE_URL, {
        headers: getAuthHeaders(options),
      }),
    [getAuthHeaders, request],
  );

  const createAccount = useCallback(
    async (payload, options = {}) =>
      request("post", EMAIL_ACCOUNTS_BASE_URL, {
        data: payload,
        headers: getAuthHeaders(options),
      }),
    [getAuthHeaders, request],
  );

  const getAccountsByUserId = useCallback(
    async ({ userId, ...options } = {}) =>
      request("get", `${EMAIL_ACCOUNTS_BASE_URL}/get`, {
        headers: getAuthHeaders(options),
        params: userId ? { userId } : undefined,
      }),
    [getAuthHeaders, request],
  );

  const getAccountById = useCallback(
    async (id, options = {}) =>
      request("get", `${EMAIL_ACCOUNTS_BASE_URL}/${id}`, {
        headers: getAuthHeaders(options),
      }),
    [getAuthHeaders, request],
  );

  const deleteAccountById = useCallback(
    async (id, options = {}) =>
      request("delete", `${EMAIL_ACCOUNTS_BASE_URL}/${id}`, {
        headers: getAuthHeaders(options),
      }),
    [getAuthHeaders, request],
  );

  const deleteAccountByEmail = useCallback(
    async ({ email, ...options } = {}) =>
      request("delete", EMAIL_ACCOUNTS_BASE_URL, {
        headers: getAuthHeaders(options),
        params: { email },
      }),
    [getAuthHeaders, request],
  );

  return {
    loading,
    error,
    // Emails
    getAllEmails,
    getEmailById,
    sendEmail,
    updateEmail,
    deleteEmail,
    deleteEmails,
    getIncomingEmailsByAccountId,
    getOutgoingEmailsByAccountId,
    getIncomingEmailsByEmail,
    getOutgoingEmailsByEmail,
    countUnreadEmails,
    // Email accounts
    getAllAccounts,
    createAccount,
    getAccountsByUserId,
    getAccountById,
    deleteAccountById,
    deleteAccountByEmail,
  };
};

export default useInhouseEmails;
